import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCountDownTimer } from '@/common/hooks/count-down-timer';
import { timeStringFormat } from '@/common/lib/timer';
import {
  H1,
  StringMain,
  StringSmall,
} from '@/components/Typography/Typography';
import { Button } from '@/components/ui/Button/Button';
import { useAppSelector } from '@/redux/store';

import { ConfirmCodeForm } from '../ConfirmCodeForm/ConfirmCodeForm';

import css from './styles.module.scss';

interface ConfirmCodeSectionProps {
  prevLoginStep: () => void;
}

const ConfirmCodeSection: FC<ConfirmCodeSectionProps> = ({ prevLoginStep }) => {
  const { t } = useTranslation();
  const email = useAppSelector((state) => state.auth.email);
  const [secondsForResendCode, setSecondsForResendCode] = useState(0);
  const timeToResend = useCountDownTimer(
    secondsForResendCode,
    (seconds: number): void => {
      if (seconds <= 0) {
        setSecondsForResendCode(0);
      }
    },
  );

  return (
    <article className={css.confirmCode}>
      <H1 className="!text-[2rem] !text-[#0E1721] leading-[90%] -tracking-[4%] mb-4 text-center">
        {t('Code')}
      </H1>
      <div className="flex items-center mb-8 gap-1">
        <StringMain className="!text-[#0E1721]">
          {t('We sent a code to')}
        </StringMain>
        <StringMain weight="bold" className="!text-[#0E1721] !leadign-1.2">
          {email}
        </StringMain>
      </div>
      <ConfirmCodeForm />
      <div className="flex w-full items-center justify-center gap-1 mb-6">
        <StringSmall className="!text-[#0E1721]">
          {t('Don’t receive the email?')}
        </StringSmall>
        {timeToResend ? (
          <StringSmall className="!text-black/40 w-[150px]">
            {t('Next resend in: ')}
            {timeStringFormat(timeToResend * 1000, {
              template: '{minutes}:{seconds}',
            })}
          </StringSmall>
        ) : (
          <StringSmall
            onClick={() => setSecondsForResendCode(60)}
            weight="bold"
            className="!text-accent cursor-pointer"
          >
            {t('Click to resend')}
          </StringSmall>
        )}
      </div>
      <Button
        onClick={prevLoginStep}
        variant="transparent"
        className="!w-auto p-2"
      >
        <StringSmall weight="bold" className="!text-accent">
          {t('Go back')}
        </StringSmall>
      </Button>
    </article>
  );
};

export { ConfirmCodeSection };
